import {Howl, Howler} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import {PredefinedPIXIAnchorPoint} from "../PredefinedPIXIAnchorPoint";
import {Card} from "./Card";
import {CardAnimation} from "./CardAnimation";
import {CardHighlightAnimation} from "./CardHighlightAnimation";
import data from "./configs/data";
import {Button} from "./controlElements/Button";
import {DoubleUpTextAnimation} from "./DoubleUpTextAnimation";
import {VideoPokerCardCombinationName} from "./VideoPokerCardCombinationName";

export class DoubleUpGame extends PIXI.Container {

    private readonly RED_BUTTON_POSITION = new PIXI.Point(448, 629);
    private readonly BLACK_BUTTON_POSITION = new PIXI.Point(835, 629);
    private readonly COLLECT_BUTTON_POSITION = new PIXI.Point(1103, 632);
    private readonly CURRENT_WIN_BACKGROUND_POSITION = new PIXI.Point(640, 629);
    private readonly CURRENT_WIN_TEXT_POSITION = new PIXI.Point(643, 646);
    private readonly DOUBLE_UP_TEXT_IMAGE_INIT_POSITION = new PIXI.Point(0, 255);
    private readonly DOUBLE_UP_WIN_VALUE_TEXT_INIT_POSITION = new PIXI.Point(0, 282);
    private readonly cardsPositions: Array<PIXI.Point> = [new PIXI.Point(220, 410), new PIXI.Point(430, 410),
        new PIXI.Point(640, 410), new PIXI.Point(851, 410), new PIXI.Point(1062, 410)];

    private readonly CURRENT_WIN_TEXT_STYLE = {
        fontSize: 26,
        fontFamily: "RobotoRegular",
        dropShadow: true,
        dropShadowAngle: 1.4,
        dropShadowBlur: 3,
        dropShadowDistance: 3,
        fill: "#fff",
        fontWeight: "bolder",
    };

    private readonly DOUBLE_UP_WIN_VALUE_TEXT_STYLE = {
        fontSize: 18,
        fontFamily: "RobotoRegular",
        dropShadow: true,
        dropShadowAngle: 1.4,
        dropShadowBlur: 3,
        dropShadowDistance: 3,
        fill: "#fff",
        fontWeight: "bolder",
    };

    private redButton: Button;
    private blackButton: Button;
    private collectButton: Button;
    private cardsShownAmount: number = 0;
    private cardsAnimations: Array<CardAnimation> = [];
    private cardHighlightAnimation: CardHighlightAnimation = new CardHighlightAnimation();
    private currentWinText: PIXI.Text;
    private overCardDoubleUpTextAnimation: DoubleUpTextAnimation;
    private overCardPotentialWinText: PIXI.Text;

    private cardsNormalDealingSpeedSound: Howl;
    private cardsFastDealingSpeedSound: Howl;

    constructor() {
        super();
        this.createAndAddBackground();
        this.createAndAddWinValueBackground();
        this.createAndAddWinValueText();
        this.createAndAddDoubleUpTextImageOverCard();
        this.createAndAddDoubleUpPotentialWinText();
        this.createAndAddRedButton();
        this.createAndAddBlackButton();
        this.createAndAddCollectButton();

        this.cardsNormalDealingSpeedSound = new Howl({
            src: [data.audios.dealingNormalSpeed],
            volume: 0.7,
        });
        this.cardsFastDealingSpeedSound = new Howl({
            src: [data.audios.dealingFastSpeed2],
            volume: 0.7,
        });
    }

    public open(videoPokerCardCombinationName: VideoPokerCardCombinationName): void {
        this.visible = true;
        this.cardsShownAmount = 0;
        this.removeCardsAnimations();
        switch (videoPokerCardCombinationName) {
            case (VideoPokerCardCombinationName.ROYAL_FLUSH):
                this.createAndAddCardsAnimations(config.maxDoublesForRoyalFlush);
                break;
            case (VideoPokerCardCombinationName.STRAIGHT_FLUSH):
                this.createAndAddCardsAnimations(config.maxDoublesForStraightFlush);
                break;
            case (VideoPokerCardCombinationName.FOUR_OF_A_KIND):
                this.createAndAddCardsAnimations(config.maxDoublesForFourOfKind);
                break;
            default:
                this.createAndAddCardsAnimations(config.maxDoublesForOtherCombination);
                break;
        }
        this.reAddCardHighlightAnimation();
        this.resetCardHighlightPosition();
        this.pulseCurrentCard();
        this.resetDoubleUpTextAndPotentialWinPosition();
    }

    public close(): void {
        this.visible = false;
    }

    public addRedButtonListener(listener: Function): void {
        this.redButton.subscribeOnClicked(listener);
    }

    public addBlackButtonListener(listener: Function): void {
        this.blackButton.subscribeOnClicked(listener);
    }

    public addCollectButtonListener(listener: Function): void {
        this.collectButton.subscribeOnClicked(listener);
    }

    public async showCard(card: Card, fastAnimationMode: boolean = false): Promise<void> {
        this.cardsAnimations[this.cardsShownAmount].setCardFront(card.getColor(), card.getName());
        if (fastAnimationMode) {
            this.cardsFastDealingSpeedSound.play();
            await this.cardsAnimations[this.cardsShownAmount].uncoverCardFast();
        } else {
            this.cardsNormalDealingSpeedSound.play();
            await this.cardsAnimations[this.cardsShownAmount].uncoverCard();
        }
        this.cardsShownAmount++;
        this.resetCardHighlightPosition();
        this.pulseCurrentCard();
        this.resetDoubleUpTextAndPotentialWinPosition();
    }

    public setCurrentPotentialWinValue(potentialWin: number, currencyType: string): void {
        this.showPotentialWinValue();
        this.currentWinText.text = potentialWin.toFixed(config.betDisplayPrecision) + " " + config.displayCurrency;
        this.overCardPotentialWinText.text = (potentialWin * 2).toFixed(config.betDisplayPrecision) + " " + config.displayCurrency;
    }

    public showPotentialWinValue(): void {
        this.overCardDoubleUpTextAnimation.visible = true;
        this.overCardPotentialWinText.visible = true;
    }

    public hidePotentialWinValue(): void {
        this.overCardDoubleUpTextAnimation.visible = false;
        this.overCardPotentialWinText.visible = false;
    }

    public enableButtons(): void {
        this.redButton.enabled = true;
        this.blackButton.enabled = true;
        this.collectButton.enabled = true;
    }

    public disableButtons(): void {
        this.redButton.enabled = false;
        this.blackButton.enabled = false;
        this.collectButton.enabled = false;
    }

    public showHighlighter(): void {
        this.cardHighlightAnimation.visible = true;
    }

    public hideHighlighter(): void {
        this.cardHighlightAnimation.visible = false;
    }

    public stopCardPulsing(): void {
        if (this.cardsAnimations[this.cardsShownAmount]) {
            this.cardsAnimations[this.cardsShownAmount].showCoveredCard();
        }
    }

    private resetCardHighlightPosition(): void {
        if (this.cardsAnimations[this.cardsShownAmount]) {
            this.cardHighlightAnimation.position = this.cardsAnimations[this.cardsShownAmount].position;
        }
    }

    private pulseCurrentCard(): void {
        if (this.cardsAnimations[this.cardsShownAmount]) {
            this.cardsAnimations[this.cardsShownAmount].pulseCard();
        }
    }

    private resetDoubleUpTextAndPotentialWinPosition(): void {
        if (this.cardsAnimations[this.cardsShownAmount]) {
            this.overCardDoubleUpTextAnimation.position.x = this.cardsAnimations[this.cardsShownAmount].position.x;
            this.overCardPotentialWinText.position.x = this.cardsAnimations[this.cardsShownAmount].position.x;
        }
    }

    private createAndAddRedButton(): void {
        this.redButton = new Button(data.images.redDoubleUpBase, data.images.redDoubleUpHover,
            data.images.redDoubleUpClick, data.images.redDoubleUpInActive, data.audios.buttonClicked);
        this.redButton.position = this.RED_BUTTON_POSITION;
        this.addChild(this.redButton);
    }

    private createAndAddBlackButton(): void {
        this.blackButton = new Button(data.images.blackDoubleUpBase, data.images.blackDoubleUpHover,
            data.images.blackDoubleUpClick, data.images.blackDoubleUpInActive, data.audios.buttonClicked);
        this.blackButton.position = this.BLACK_BUTTON_POSITION;
        this.addChild(this.blackButton);
    }

    private createAndAddCollectButton(): void {
        this.collectButton = new Button(data.images.collectBase, data.images.collectHover,
            data.images.collectClick, data.images.collectInActive, data.audios.collectButtonClicked);
        this.collectButton.position = this.COLLECT_BUTTON_POSITION;
        this.addChild(this.collectButton);
    }

    private createAndAddCardsAnimations(amount: number): void {
        switch (amount) {
            case 1:
                this.createAndAddCardBackground(this.cardsPositions[2]);
                this.createAndAddCard(this.cardsPositions[2]);
                break;
            case 2:
                this.createAndAddCardBackground(this.cardsPositions[1]);
                this.createAndAddCard(this.cardsPositions[1]);
                this.createAndAddCardBackground(this.cardsPositions[2]);
                this.createAndAddCard(this.cardsPositions[2]);
                break;
            case 3:
                this.createAndAddCardBackground(this.cardsPositions[1]);
                this.createAndAddCard(this.cardsPositions[1]);
                this.createAndAddCardBackground(this.cardsPositions[2]);
                this.createAndAddCard(this.cardsPositions[2]);
                this.createAndAddCardBackground(this.cardsPositions[3]);
                this.createAndAddCard(this.cardsPositions[3]);
                break;
            case 4:
                this.createAndAddCardBackground(this.cardsPositions[0]);
                this.createAndAddCard(this.cardsPositions[0]);
                this.createAndAddCardBackground(this.cardsPositions[1]);
                this.createAndAddCard(this.cardsPositions[1]);
                this.createAndAddCardBackground(this.cardsPositions[2]);
                this.createAndAddCard(this.cardsPositions[2]);
                this.createAndAddCardBackground(this.cardsPositions[3]);
                this.createAndAddCard(this.cardsPositions[3]);
                break;
            case 5:
                this.createAndAddCardBackground(this.cardsPositions[0]);
                this.createAndAddCard(this.cardsPositions[0]);
                this.createAndAddCardBackground(this.cardsPositions[1]);
                this.createAndAddCard(this.cardsPositions[1]);
                this.createAndAddCardBackground(this.cardsPositions[2]);
                this.createAndAddCard(this.cardsPositions[2]);
                this.createAndAddCardBackground(this.cardsPositions[3]);
                this.createAndAddCard(this.cardsPositions[3]);
                this.createAndAddCardBackground(this.cardsPositions[4]);
                this.createAndAddCard(this.cardsPositions[4]);
                break;
        }
    }

    private removeCardsAnimations(): void {
        for (let i = 0; i < this.cardsAnimations.length; i++) {
            this.removeChild(this.cardsAnimations[i]);
        }
        this.cardsAnimations = [];
    }

    private reAddCardHighlightAnimation(): void {
        this.removeChild(this.cardHighlightAnimation);
        this.addChild(this.cardHighlightAnimation);
    }

    private createAndAddCard(position: PIXI.Point): void {
        let cardAnimation: CardAnimation;
        cardAnimation = new CardAnimation();
        cardAnimation.position = position;
        cardAnimation.showCoveredCard();
        this.cardsAnimations.push(cardAnimation);
        this.addChild(cardAnimation);
    }

    private createAndAddCardBackground(position: PIXI.Point): void {
        let cardBackground: PIXI.Sprite;
        cardBackground = PIXI.Sprite.fromImage(data.images.doubleUpCardBackground);
        cardBackground.position = position;
        cardBackground.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(cardBackground);
    }

    private createAndAddWinValueBackground(): void {
        const currentWinBackground: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.doubleUpYouWonTextWithBg);
        currentWinBackground.position = this.CURRENT_WIN_BACKGROUND_POSITION;
        currentWinBackground.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(currentWinBackground);
    }

    private createAndAddWinValueText(): void {
        this.currentWinText = new PIXI.Text("", this.CURRENT_WIN_TEXT_STYLE);
        this.currentWinText.position = this.CURRENT_WIN_TEXT_POSITION;
        this.currentWinText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.addChild(this.currentWinText);
    }

    private createAndAddDoubleUpTextImageOverCard(): void {
        this.overCardDoubleUpTextAnimation = new DoubleUpTextAnimation();
        this.overCardDoubleUpTextAnimation.position = this.DOUBLE_UP_TEXT_IMAGE_INIT_POSITION;
        this.addChild(this.overCardDoubleUpTextAnimation);
    }

    private createAndAddDoubleUpPotentialWinText(): void {
        this.overCardPotentialWinText = new PIXI.Text("", this.DOUBLE_UP_WIN_VALUE_TEXT_STYLE);
        this.overCardPotentialWinText.anchor = PredefinedPIXIAnchorPoint.CENTER;
        this.overCardPotentialWinText.position = this.DOUBLE_UP_WIN_VALUE_TEXT_INIT_POSITION;
        this.addChild(this.overCardPotentialWinText);
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.doubleUpGameBackground);
        background.interactive = true;
        this.addChild(background);
    }
}



// WEBPACK FOOTER //
// ./src/app/DoubleUpGame.ts